/**
 * Which attachment types may be shown inline, in the host's own origin.
 *
 * The type of an attachment is whatever the uploader declared. Served or opened with that
 * type, an HTML, SVG, XML or JavaScript file runs as a page of the host — whoever opens a
 * ticket (an owner, an admin) runs the uploader's script with their own session. Only the
 * types below are shown; everything else is a download (0.14.3).
 */
export const INLINE_ATTACHMENT_TYPES: readonly string[] = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/avif",
  "video/mp4",
  "video/webm",
  "application/pdf",
  "text/plain",
];

/** The type of everything that is not safe inline: the browser saves it, never renders it. */
export const DOWNLOAD_TYPE = "application/octet-stream";

/** The declared type when it is safe inline (without parameters), otherwise `null`. */
export const inlineType = (mime: string | null | undefined): string | null => {
  const base = (mime ?? "").split(";")[0].trim().toLowerCase();
  return INLINE_ATTACHMENT_TYPES.includes(base) ? base : null;
};

/** Response headers for an attachment's bytes: inline if safe, a download otherwise. */
export const attachmentHeaders = (
  mime: string | null | undefined,
  name: string,
): Record<string, string> => {
  const inline = inlineType(mime);
  if (inline) return { "Content-Type": inline, "X-Content-Type-Options": "nosniff" };
  const safeName = name.replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 100) || "allegato";
  return {
    "Content-Type": DOWNLOAD_TYPE,
    "Content-Disposition": `attachment; filename="${safeName}"`,
    "X-Content-Type-Options": "nosniff",
  };
};
